// src/lib/automation/handlers.js
// ─────────────────────────────────────────────────────────────────────────────
//  All automation rule handlers — registered once at app boot.
//
//  PATCHES (this version):
//
//    REMOVED: All milestone-related logic (addMilestone, checkCountMilestone,
//      and all calls to them across every handler). The milestones store and
//      Growth Hub feature have been removed from the app. Calendar events,
//      habitFlags, contact updates, and verse clipboard wires are unchanged.
//
//    FIX 🔴 BUG-D5 (previous): Added TITHE_RECORDED handler.
//      The event was defined in eventTypes.js but had no handler — any page
//      that called emit(EVENTS.TITHE_RECORDED) was silently a no-op.
//      The handler now writes habitFlags.titheRecordedToday so the global
//      challenge sync in Layout (habit_expense → stewardship category) can
//      detect the event and auto check-in the tithe_commitment challenge.
//
//    FIX 🔴 (Layer 4 audit): Module-level on() calls fired twice in React
//      StrictMode and on any accidental module re-evaluation (e.g. code-split
//      chunk re-load). Each extra registration accumulated a duplicate entry in
//      _handlers[eventType], causing every emit() to invoke each handler N times
//      silently — duplicating calendar events and contact updates.
//
//      Fix: all on() calls are now wrapped in registerHandlers(), guarded by a
//      module-level `_registered` flag. Subsequent calls to registerHandlers()
//      are no-ops. The flag is also exported as resetHandlersForTesting() so
//      unit tests can wipe state between runs.
//
//    FIX 🟡 (Layer 4 audit): todayKey() used new Date().toISOString() which
//      returns UTC midnight. For UTC+8 users (Philippines) this is 8 hours
//      behind local midnight — between midnight and 8AM local, todayKey()
//      returned yesterday's date while the rest of the app used local time via
//      formatDate(). Calendar events created in that window were stamped with
//      the wrong date.
//
//      Fix: todayKey() now uses localDateStr() — pure local-time arithmetic
//      that matches formatDate(new Date()) without importing dateUtils.
//      followUpDate in GOSPEL_STAGE_ADVANCED also switched to localDateStr().
//
//  HANDLER SIGNATURES:
//    Each handler: (payload, stores) => void
//    stores[key].get()  → current value
//    stores[key].set(v) → update value (triggers React re-render + sync)
// ─────────────────────────────────────────────────────────────────────────────
import { on } from './triggerEngine';
import { EVENTS } from './eventTypes';

// ── Registration guard ────────────────────────────────────────────────────────
// FIX: Prevents double-registration in React StrictMode (dev) and on any
// module re-evaluation. Without this guard, each emit() fires handlers N times
// because _handlers accumulates duplicates with no deduplication.
let _registered = false;

/** @internal — exported for unit test teardown only. */
export function resetHandlersForTesting() {
  _registered = false;
}

// ── Date helpers ──────────────────────────────────────────────────────────────
// FIX: Use local calendar date instead of UTC.
// toISOString() returns UTC midnight — for UTC+8 (Philippines), between midnight
// and 8AM local time the UTC date is still yesterday. All other hooks use
// formatDate() (local time). We replicate the same arithmetic here without
// importing dateUtils to keep handlers.js dependency-free.
function localDateStr(d = new Date()) {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${dy}`;
}

const todayKey = () => localDateStr();
const uid      = () => Date.now().toString();

// ── Helper: add a calendar event (dedup by id) ───────────────────────────────
function addCalendarEvent(stores, event) {
  const store = stores.calendarEvents;
  if (!store) return;
  store.set(prev => {
    const arr = Array.isArray(prev) ? prev : [];
    if (arr.some(e => e.id === event.id)) return prev;
    return [...arr, event];
  });
}

// ── Handler registration (idempotent) ─────────────────────────────────────────
function registerHandlers() {
  if (_registered) return;
  _registered = true;

  // ═══════════════════════════════════════════════════════════════════════════
  //  1. ATTENDANCE_MARKED — Discipleship session recorded
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.ATTENDANCE_MARKED, (payload, stores) => {
    // Wire: mark habit flag so useGlobalChallengeSync can auto-check discipleship challenges
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), discipleMetToday: todayKey() }));
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  2. GOSPEL_STAGE_ADVANCED
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.GOSPEL_STAGE_ADVANCED, (payload, stores) => {
    const { contactId, contactName, newStage, method } = payload;
    const today = todayKey();

    // Wire: Create Calendar follow-up event in 7 days
    // FIX: Use local date arithmetic to avoid UTC rollback for UTC+8 users.
    const followUpDate = new Date();
    followUpDate.setDate(followUpDate.getDate() + 7);
    const followUpKey = localDateStr(followUpDate);

    addCalendarEvent(stores, {
      id:          `gospel-followup-${contactId || uid()}-${followUpKey}`,
      title:       `Follow up with ${contactName} — gospel stage: ${newStage}`,
      date:        followUpKey,
      color:       'blue',
      sourceType:  'people',
      sourceRoute: '/stewardship/people',
      sourceId:    contactId,
      description: method ? `Method used: ${method}` : '',
    });

    // Wire: Set habitFlag for evangelism
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), evangelismToday: today, contactedToday: today }));
    }

    // Wire: Update the contact's gospel stage in People Keeper
    if (stores.peopleContacts && contactId) {
      stores.peopleContacts.set(prev => {
        if (!Array.isArray(prev)) return prev;
        return prev.map(c => {
          if (c.id !== contactId) return c;
          return {
            ...c,
            gospelStage:  newStage,
            gospelMethod: method || c.gospelMethod,
            gospelShared: true,
            updatedAt:    new Date().toISOString(),
          };
        });
      });
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  3. SERMON_SAVED
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.SERMON_SAVED, (payload, stores) => {
    const { date, title, speaker } = payload;
    const today = date || todayKey();

    // Wire: Add Calendar event with sourceRoute
    addCalendarEvent(stores, {
      id:          `sermon-${today}-${uid()}`,
      title:       `Sermon: ${title || speaker || 'Notes'}`,
      date:        today,
      color:       'blue',
      sourceType:  'sermon',
      sourceRoute: '/sermon-notes',
      description: speaker ? `Speaker: ${speaker}` : '',
    });

    // Wire: habitFlag so useGlobalChallengeSync detects the sermon save
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), sermonSavedToday: today }));
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  4. EXPENSE_RECORDED
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.EXPENSE_RECORDED, (payload, stores) => {
    const { contactId, contactName, amount, category, isRecurring, recurringDate } = payload;

    // Wire: If expense linked to a person, add transaction ref to their record
    if (stores.peopleContacts && contactId) {
      stores.peopleContacts.set(prev => {
        if (!Array.isArray(prev)) return prev;
        return prev.map(c => {
          if (c.id !== contactId) return c;
          const transactions = Array.isArray(c.transactions) ? c.transactions : [];
          return {
            ...c,
            transactions: [...transactions, {
              id:       uid(),
              date:     todayKey(),
              amount:   amount,
              category: category,
              type:     'expense',
            }],
            updatedAt: new Date().toISOString(),
          };
        });
      });
    }

    // Wire: Recurring expense → Calendar reminder for next due date
    if (isRecurring && recurringDate) {
      addCalendarEvent(stores, {
        id:          `expense-recurring-${uid()}`,
        title:       `Expense due: ${category}`,
        date:        recurringDate,
        color:       'amber',
        sourceType:  'expense',
        sourceRoute: '/stewardship/expenses',
        description: contactName ? `For ${contactName} — ₱${amount}` : `₱${amount}`,
      });
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  5. TITHE_RECORDED
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.TITHE_RECORDED, (payload, stores) => {
    const { date } = payload;
    const today = date || todayKey();

    // Wire: habitFlag so useGlobalChallengeSync detects the tithe
    // Uses 'titheRecordedToday' for deduplication and falls into
    // the same expense detection branch (expenseCountToday / stewardship).
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), titheRecordedToday: today }));
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  6. DISCIPLE_MISSED_SESSIONS
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.DISCIPLE_MISSED_SESSIONS, (payload, stores) => {
    const { discipleId, discipleName, missedCount } = payload;
    const today = todayKey();

    addCalendarEvent(stores, {
      id:          `missed-followup-${discipleId}-${today}`,
      title:       `Follow up: ${discipleName} missed ${missedCount} sessions`,
      date:        today,
      color:       'rose',
      sourceType:  'discipleship',
      sourceRoute: '/discipleship',
      sourceId:    discipleId,
      description: `${discipleName} has missed ${missedCount} consecutive sessions. Reach out!`,
    });

    if (stores.peopleContacts && discipleId) {
      stores.peopleContacts.set(prev => {
        if (!Array.isArray(prev)) return prev;
        return prev.map(c => {
          if (c.id !== discipleId) return c;
          return {
            ...c,
            followUpFlag: `Missed ${missedCount} discipleship sessions`,
            followUpDate: today,
            updatedAt:    new Date().toISOString(),
          };
        });
      });
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  7. VERSE_HIGHLIGHTED — Bible verse saved to clipboard
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.VERSE_HIGHLIGHTED, (payload, stores) => {
    const { reference, text } = payload;

    if (stores.verseClipboard) {
      stores.verseClipboard.set(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        if (arr.some(v => v.reference === reference)) return prev;
        return [{ reference, text, date: todayKey(), id: uid() }, ...arr].slice(0, 20);
      });
    }

    // Highlighting a verse is not Bible reading, so it no longer stamps bibleReadToday.
    // Reading is credited when a chapter is read (Bible.jsx markChapterRead).
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  8. PRAYER_CHECKIN — Prayer streak tracking
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.PRAYER_CHECKIN, (_payload, _stores) => {
    // Streak milestone wires removed. Future streak UI should read directly
    // from prayerCheckins rather than relying on milestone records.
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  9. BIBLE_STUDY_COMPLETED
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.BIBLE_STUDY_COMPLETED, (payload, stores) => {
    const { date, title, attendeeCount } = payload;
    const today = date || todayKey();

    // Wire: Calendar event with sourceRoute
    addCalendarEvent(stores, {
      id:          `biblestudy-${today}-${uid()}`,
      title:       `Bible Study: ${title || 'Session'}`,
      date:        today,
      color:       'amber',
      sourceType:  'discipleship',
      sourceRoute: '/discipleship/bible-study-guide',
      description: attendeeCount ? `${attendeeCount} attendees` : '',
    });

    // Wire: habitFlags for both bibleStudy and bible reading (same action)
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({
        ...(prev || {}),
        bibleStudyToday: today,
        bibleReadToday:  today,
      }));
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  10. BIBLE_READING_ADVANCED — Reading plan progress
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.BIBLE_READING_ADVANCED, (_payload, stores) => {
    // Wire: habitFlag so useGlobalChallengeSync detects Bible reading
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), bibleReadToday: todayKey() }));
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  11. JOURNAL_WRITTEN
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.JOURNAL_WRITTEN, (payload, stores) => {
    const { mentionedPersons } = payload;

    // Wire: habitFlag so useGlobalChallengeSync detects journal entry
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), journalEditedToday: todayKey() }));
    }

    // Wire: Journal @mentions → link to People Keeper contacts
    if (stores.peopleContacts && Array.isArray(mentionedPersons) && mentionedPersons.length > 0) {
      stores.peopleContacts.set(prev => {
        if (!Array.isArray(prev)) return prev;
        return prev.map(c => {
          const mentioned = mentionedPersons.find(p =>
            p.toLowerCase() === (c.name || '').toLowerCase()
          );
          if (!mentioned) return c;
          const interactions = Array.isArray(c.interactions) ? c.interactions : [];
          return {
            ...c,
            interactions: [...interactions, {
              id:   uid(),
              type: 'journal_mention',
              date: todayKey(),
              note: 'Mentioned in journal entry',
            }],
            updatedAt: new Date().toISOString(),
          };
        });
      });
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  //  12. CONTACT_ADDED — New contact or contact interaction
  // ═══════════════════════════════════════════════════════════════════════════
  on(EVENTS.CONTACT_ADDED, (payload, stores) => {
    const today = todayKey();

    // Wire: Person contacted → mark habit flag → Dashboard count
    if (stores.habitFlags) {
      stores.habitFlags.set(prev => ({ ...(prev || {}), contactedToday: today }));
    }
  });
}

// ── Boot — register all handlers once ────────────────────────────────────────
// This module is imported as a side-effect in useTriggerEngine.js:
//   import '@/lib/automation/handlers';
// Calling registerHandlers() here is idempotent — the _registered guard
// ensures handlers accumulate only once regardless of how many times the
// module initialiser runs (React StrictMode, HMR, etc.).
registerHandlers();